import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { ArrowRight, Radio } from "lucide-react";
import OutletFavicon from "@/components/OutletFavicon";
import ArticlePeekDialog from "@/components/ArticlePeekDialog";
import GradualBlur from "@/components/reactbits/GradualBlur";
import ShinyText from "@/components/reactbits/ShinyText";
import PixelCard, { pixelInk } from "@/components/reactbits/PixelCard";
import { getTicker, leanStyle } from "@/lib/api";
import { useTheme } from "@/lib/theme";

const ROWS = 2;
// Seconds each card spends crossing its own width — so the speed stays the same
// whether the feed has eight articles or eighty. Higher is slower. At 9s a
// headline is readable without chasing it, which is the point of the strip.
const SECONDS_PER_CARD = 9;

// isDark is passed in rather than read from useTheme() here: the track renders
// up to eighty of these, and eighty subscriptions to the same store to learn the
// same fact is eighty re-renders where one would do.
function TickerCard({ item, onPick, duplicate = false, isDark = false }) {
  const s = item.scored ? leanStyle(item.lean_label) : null;
  return (
    <button
      type="button"
      onClick={() => onPick(item.id)}
      data-testid={`ticker-card-${item.id}`}
      // The second copy of the track exists only to make the loop seamless.
      // Left visible to assistive tech it would read every headline twice and
      // add a second tab stop for each.
      aria-hidden={duplicate || undefined}
      tabIndex={duplicate ? -1 : undefined}
      // The press scale composes with the track's own translate rather than
      // fighting it — and the track is already paused by the time a pointer is
      // down, since it stops on hover and focus-within.
      // Padding and the flex column move down to PixelCard so the canvas covers
      // the whole card; overflow-hidden keeps it inside the rounded corners.
      className="group mx-1.5 w-[280px] shrink-0 overflow-hidden rounded-xl border border-line/10 bg-surface text-left transition-[transform,border-color] duration-150 ease-out hover:border-line/30 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-ink sm:w-[320px]"
    >
      <PixelCard className="h-full" gap={6} colors={pixelInk(isDark)}
        contentClassName="pixel-legible flex h-full flex-col justify-between p-4 transition-transform duration-200 ease-out group-hover:-translate-y-0.5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-faint">
          <OutletFavicon url={item.url} outlet={item.source} size={14} />
          <span className="truncate">{item.source}</span>
        </span>
        {item.scored ? (
          <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${s.text} ${s.bg} ${s.border}`}>
            {item.lean_label}
          </span>
        ) : (
          <span className="shrink-0 rounded-full border border-line/15 bg-subtle px-2 py-0.5 text-[10px] font-semibold text-faint">
            Unscored
          </span>
        )}
      </div>
      <p className="mt-2 line-clamp-3 font-serif text-[15px] font-bold leading-snug text-ink group-hover:underline">
        {item.title}
      </p>
      </PixelCard>
    </button>
  );
}

/**
 * The live wire: ingested headlines scrolling past, click one to score it.
 *
 * Nothing here has cost anything yet — ingestion only stores metadata. The
 * cards marked with a lean are ones somebody already paid to analyze, and are
 * free for everyone to read.
 */
export default function NewsTicker({ limit = 40 }) {
  // Canvas takes concrete colours, so this one can't be a CSS variable.
  const isDark = useTheme() === "dark";
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState(null);
  const [rotationMinutes, setRotationMinutes] = useState(5);
  const reduceMotion = useReducedMotion();

  const load = useCallback(() => {
    getTicker(limit)
      .then((data) => {
        setItems(data.items || []);
        if (data.rotation_minutes) setRotationMinutes(data.rotation_minutes);
      })
      .catch(() => { /* the ticker is decoration; a failure must not break the page */ });
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  // The backend rotates its window on a fixed clock, so re-fetching on that
  // same cadence is what keeps the strip turning over.
  useEffect(() => {
    const id = setInterval(load, rotationMinutes * 60 * 1000);
    return () => clearInterval(id);
  }, [load, rotationMinutes]);

  const rows = useMemo(() => {
    if (!items.length) return [];
    const out = Array.from({ length: ROWS }, () => []);
    items.forEach((item, i) => out[i % ROWS].push(item));
    return out.filter((r) => r.length);
  }, [items]);

  if (!items.length) return null;

  return (
    <section className="border-y border-line/10 bg-subtle/60 py-8" data-testid="news-ticker">
      <div className="mx-auto mb-5 flex max-w-6xl items-end justify-between px-5">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest">
            <Radio size={13} className="text-emerald-500" />
            <ShinyText text="Live wire" speed={4} color="rgb(var(--c-faint))" shineColor="rgb(var(--c-ink))" />
          </p>
          <h2 className="mt-1 font-serif text-2xl font-bold text-ink">
            Today's news, waiting to be scored
          </h2>
          <p className="mt-1 text-sm text-faint">
            Click any headline for a preview — analyze it and the score is cached for everyone.
          </p>
        </div>
        <Link to="/feed" data-testid="ticker-see-all"
          className="hidden items-center gap-1 text-sm font-semibold text-ink hover:underline sm:flex">
          Browse all <ArrowRight size={15} />
        </Link>
      </div>

      {reduceMotion ? (
        // No animation for anyone who asked not to have any: same cards, laid
        // out as a plain grid.
        <div className="mx-auto grid max-w-6xl gap-3 px-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="ticker-static">
          {items.slice(0, 9).map((item) => (
            <TickerCard key={item.id} item={item} onPick={setPicked} isDark={isDark} />
          ))}
        </div>
      ) : (
        // Blurred at both ends so cards dissolve in and out instead of being
        // guillotined by the viewport edge.
        <div className="relative flex flex-col gap-3">
          {rows.map((row, i) => (
            <div key={i} className="ticker-row overflow-hidden" data-testid={`ticker-row-${i}`}>
              <div
                className={`ticker-track ${i % 2 ? "ticker-track-reverse" : ""}`}
                style={{ "--ticker-duration": `${Math.max(30, row.length * SECONDS_PER_CARD)}s` }}
              >
                {/* Rendered twice: the track travels exactly half its width, so
                    the copy lands where the original began and the loop is seamless. */}
                {[...row, ...row].map((item, j) => (
                  <TickerCard key={`${item.id}-${j}`} item={item} onPick={setPicked}
                    duplicate={j >= row.length} isDark={isDark} />
                ))}
              </div>
            </div>
          ))}
          {/* 7rem a side is a tenth of a desktop viewport and more than half a
              phone's: two 112px veils on a 390px screen left about 165px of
              legible strip in the middle, which is narrower than one 280px
              card — so on a phone every headline was partly dissolved all of
              the time. GradualBlur's own responsive mode swaps in a narrower
              veil at the two breakpoints it knows (<=480 and <=768) and leaves
              the desktop width exactly as it was. */}
          <GradualBlur target="parent" position="left" width="7rem" strength={1.5}
            responsive tabletWidth="4rem" mobileWidth="2.5rem"
            divCount={5} curve="ease-out" opacity={1} />
          <GradualBlur target="parent" position="right" width="7rem" strength={1.5}
            responsive tabletWidth="4rem" mobileWidth="2.5rem"
            divCount={5} curve="ease-out" opacity={1} />
        </div>
      )}

      <ArticlePeekDialog articleId={picked} open={!!picked}
        onOpenChange={(o) => !o && setPicked(null)} />
    </section>
  );
}
